import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../app/store';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Post, deletePost, getGroupPosts } from '../../../../app/features/sessions/sessionPostProject/sessionPostProject';
import { UserAvatar } from '../../../../app/components/userAvatar/UserAvatar';
import { CircularProgress } from '@mui/material';
import ProjectDetailsModal from '../../../../app/components/modal/projectModal/ProjectDetails';
import { ProjectPlayer } from '../../../../app/components/audioPlayer/WaveSurfer/projectPlayer/ProjectPlayer';
import { format } from 'date-fns';
import React from 'react';
import CommentsInPost from '../../../../app/components/comments/CommentsInPost';
import CreateCommentInPostForm from '../../../../app/components/forms/post/comment/CreateCommentInPostForm';
import LoadingModal from '../../../../app/components/modal/loadingModal/LoadingModal';
import {
    Box,
    Button,
    Chip,
    Collapse,
    IconButton,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

export default function PostsComponent() {
    const groupPosts         = useSelector((state: RootState) => state.sessionProjectPosts.groupPosts);
    const newPosts           = useSelector((state: RootState) => state.sessionProjectPosts.newPosts);
    const [postsPage, setPostsPage] = useState(1);
    const authHeaders        = useSelector((state: RootState) => state.session.authHeaders);
    const currentUser        = useSelector((state: RootState) => state.session.currentUser);
    const loadingDeleteComment = useSelector((state: RootState) => state.sessionProjectPosts.loadingDeletePost);
    const dispatch           = useDispatch<AppDispatch>();
    const [projectSelected, setProjectSelected] = useState<any>();
    const { userNicknameRoute, groupId } = useParams();
    const [loadingMorePostsPages, setLoadingMorePostsPages] = useState(false);
    const [showProjectDetails, setShowProjectDetails] = useState(false);
    const [postToCommentSelected, setPostToCommentSelected] = useState('');
    const loadingMorePosts   = useSelector((state: RootState) => state.sessionProjectPosts.loadingMorePosts);
    const loadingCreatePost  = useSelector((state: RootState) => state.sessionProjectPosts.loadingCreatePost);
    const MemorizedProjectPlayer = useMemo(() => React.memo(ProjectPlayer), []);

    useEffect(() => {
        async function getPosts() {
            if (authHeaders && userNicknameRoute && groupId) {
                await dispatch(
                    getGroupPosts({
                        authHeaders: authHeaders,
                        getGroupPostsRouteParams: {
                            userNickname: userNicknameRoute,
                            groupId: groupId,
                            page: `${postsPage}`,
                        },
                    })
                );
            }
        }

        getPosts();

        if (groupPosts.length === 0) {
            getPosts();
        }
    }, [postsPage]);

    useEffect(() => {
        const handleScroll = () => {
            const container = document.getElementById('container-ul-group-posts');
            if (!container) return;

            const { scrollTop, clientHeight, scrollHeight } = container;
            if (scrollTop + clientHeight >= scrollHeight && !loadingMorePostsPages) {
                setPostsPage(postsPage + 1);
            }
        };

        setTimeout(() => {
            const container = document.getElementById('container-ul-group-posts');
            if (container) {
                container.addEventListener('scroll', handleScroll);
                return () => container.removeEventListener('scroll', handleScroll);
            }
        }, 100);
    }, [loadingMorePosts, loadingCreatePost]);

    function toggleDescriptionModal(projectSelected: any) {
        setShowProjectDetails(!showProjectDetails);
        setProjectSelected(projectSelected);
    }

    function handleFormatDate(date: any) {
        const originalDate = new Date(date);
        return format(originalDate, 'dd MMM yyyy HH:mm');
    }

    function openCommentContainer(postId: string) {
        if (postId !== postToCommentSelected) {
            setPostToCommentSelected(postId);
        } else {
            setPostToCommentSelected('');
        }
    }

    async function handleDeletePost(postId: string, groupId: string) {
        if (authHeaders && currentUser?.nickname) {
            dispatch(
                deletePost({
                    authHeaders: authHeaders,
                    routeParams: {
                        userNickname: currentUser.nickname,
                        postId: postId,
                        groupId: groupId,
                    },
                })
            );
        }
    }

    function renderPost(item: Post, index: number) {
        const isOwner = item.user?.id === currentUser?.id || item.user?.userMoreInfo?.nickname === currentUser?.nickname;
        const commentOpen = postToCommentSelected === `${item.id}` && item.id != undefined;
        const hasProject = item.project?.project != undefined;

        return (
            <Box
                key={index}
                sx={{
                    bgcolor: BG_SECTION,
                    border: BORDER_SUBTLE,
                    borderRadius: '10px',
                    mb: 1.5,
                    overflow: 'hidden',
                }}
            >
                {/* Cabeçalho do post */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: { xs: 1.5, sm: 2 },
                        pt: 1.5,
                        pb: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <UserAvatar src={item.user?.userMoreInfo?.avatarUrl} size={34} />
                        <Box>
                            <Typography
                                variant='subtitle2'
                                fontWeight={700}
                                color={TEXT_PRIMARY}
                                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                            >
                                {item.user?.userMoreInfo?.nickname}
                            </Typography>
                            <Typography variant='caption' color={TEXT_SECONDARY} sx={{ fontSize: '0.7rem' }}>
                                {handleFormatDate(item.createdAt)}
                            </Typography>
                        </Box>
                    </Box>

                    {isOwner && (
                        <IconButton
                            size='small'
                            onClick={() => handleDeletePost(`${item.id}`, `${item.groupId}`)}
                            sx={{
                                color: '#e57373',
                                '&:hover': { bgcolor: 'rgba(192,57,43,0.12)' },
                            }}
                        >
                            <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                    )}
                </Box>

                {/* Corpo do post */}
                {item.body && (
                    <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1.5 }}>
                        <Typography
                            variant='body2'
                            color={TEXT_PRIMARY}
                            sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontSize: { xs: '0.825rem', sm: '0.875rem' },
                            }}
                        >
                            {item.body}
                        </Typography>
                    </Box>
                )}

                {/* Projecto anexado */}
                {hasProject && (
                    <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1.5 }}>
                        <Box
                            sx={{
                                bgcolor: BG_CARD,
                                borderRadius: '8px',
                                border: BORDER_SUBTLE,
                                overflow: 'hidden',
                            }}
                        >
                            {/* Topo do projecto */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 1.5,
                                    pt: 1.25,
                                    pb: 1,
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    variant='subtitle2'
                                    fontWeight={600}
                                    color={TEXT_PRIMARY}
                                    noWrap
                                    sx={{ flex: 1, minWidth: 0, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                >
                                    {item.project!.project.title}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                        label={item.project!.project.privacity === 0 ? 'PUBLIC' : 'PRIVATE'}
                                        size='small'
                                        sx={{
                                            bgcolor: item.project!.project.privacity === 0
                                                ? 'rgba(35,148,0,0.18)'
                                                : 'rgba(192,57,43,0.18)',
                                            color: item.project!.project.privacity === 0 ? '#4caf50' : '#e57373',
                                            fontWeight: 700,
                                            fontSize: '0.6rem',
                                        }}
                                    />
                                    <IconButton
                                        size='small'
                                        onClick={() => toggleDescriptionModal(item.project)}
                                        sx={{
                                            color: TEXT_SECONDARY,
                                            '&:hover': { color: TEXT_PRIMARY },
                                        }}
                                    >
                                        <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Box>
                            </Box>

                            {/* Player */}
                            {item.project!.masterFiles && item.project!.masterFiles.length > 0 && (
                                <Box sx={{ px: 1.5, pb: 1.5 }}>
                                    <MemorizedProjectPlayer
                                        fileUrl={`${item.project!.masterFiles[0].signedUrl}`}
                                    />
                                </Box>
                            )}

                            {/* Botão comentar */}
                            <Box sx={{ borderTop: BORDER_SUBTLE }}>
                                <Button
                                    fullWidth
                                    size='small'
                                    startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: '16px !important' }} />}
                                    onClick={() => openCommentContainer(`${item.id}`)}
                                    sx={{
                                        color: commentOpen ? TEXT_PRIMARY : TEXT_SECONDARY,
                                        textTransform: 'none',
                                        fontWeight: commentOpen ? 600 : 400,
                                        bgcolor: commentOpen ? 'rgba(255,255,255,0.06)' : 'transparent',
                                        borderRadius: 0,
                                        py: 0.75,
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: TEXT_PRIMARY },
                                    }}
                                >
                                    Comment
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Secção de comentários (apenas quando não há projecto anexado) */}
                {!hasProject && (
                    <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1, borderTop: BORDER_SUBTLE }}>
                        <Button
                            size='small'
                            startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: '16px !important' }} />}
                            onClick={() => openCommentContainer(`${item.id}`)}
                            sx={{
                                color: commentOpen ? TEXT_PRIMARY : TEXT_SECONDARY,
                                textTransform: 'none',
                                fontWeight: commentOpen ? 600 : 400,
                                '&:hover': { color: TEXT_PRIMARY },
                                mt: 0.5,
                            }}
                        >
                            Comment
                        </Button>
                    </Box>
                )}

                {/* Comentários expansíveis */}
                <Collapse in={commentOpen}>
                    <Box
                        sx={{
                            borderTop: BORDER_SUBTLE,
                            bgcolor: BG_CARD,
                            px: { xs: 1.5, sm: 2 },
                            py: 1.5,
                        }}
                    >
                        <CommentsInPost groupId={`${item.groupId}`} postId={`${item.id}`} />
                        <Box sx={{ mt: 1 }}>
                            <CreateCommentInPostForm postId={`${item.id}`} />
                        </Box>
                    </Box>
                </Collapse>
            </Box>
        );
    }

    const allPosts = [...(newPosts ?? []), ...(groupPosts ?? [])];

    return (
        <>
            {(loadingDeleteComment === true || loadingCreatePost === true) ? (
                <LoadingModal />
            ) : (
                <Box
                    id='container-ul-group-posts'
                    sx={{
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        pr: 0.5,
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(255,255,255,0.15) transparent',
                    }}
                >
                    {allPosts.map((item, index) => renderPost(item, index))}

                    {loadingMorePosts && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} sx={{ color: BRAND_RED }} />
                        </Box>
                    )}

                    {showProjectDetails && (
                        <ProjectDetailsModal
                            projectInfoAndUrls={projectSelected}
                            close={() => setShowProjectDetails(!showProjectDetails)}
                        />
                    )}
                </Box>
            )}
        </>
    );
}
